<script setup lang="ts">
    import { ref, computed, watch, toRef } from 'vue'

    import { EditorState } from '@common/Common'
    import type { SurveyEditorDialogViewModel } from '@Evaluation/CriteriaSurveys/Components/SurveyEditorDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: SurveyEditorDialogViewModel
    }>()

    const emit = defineEmits<{
        (
            e: 'create-survey',
            surveyId: number | null,
            name: string | null,
            description: string | null,
            responseDueDate: string | null
        ): void
        (
            e: 'update-survey',
            surveyId: number | null,
            name: string | null,
            description: string | null,
            responseDueDate: string | null
        ): void
        (e: 'delete-survey', surveyId: number | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived (VM passthrough) ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const state = computed(() => vm.value.editorState)
    const title = computed(() => vm.value.editorTitle)
    const nameError = computed(() => vm.value.nameError)

    // writable computed (mirrors getter/setter)
    const responseDueDate = computed<string | null>({
        get: () => vm.value.responseDueDate,
        set: val => { vm.value.responseDueDate = val }
    })

    // ---------------- local form state ----------------

    const Initialized = ref(false)

    const SurveyId = ref<number | null>(null)
    const Name = ref<string | null>(null)
    const Description = ref<string | null>(null)

    // ---------------- watch ----------------

    watch(state, (val, oldVal) =>
    {
        if (val === EditorState.Closed)
        {
            Initialized.value = false
        }
        else
        {
            vm.value.clearValidationErrors()

            SurveyId.value = vm.value.surveyId
            Name.value = vm.value.surveyName
            Description.value = vm.value.surveyDescription

            Initialized.value = true
        }
    })

    // ---------------- actions ----------------

    function CreateCommand()
    {
        vm.value.clearValidationErrors()
        emit(
            'create-survey',
            SurveyId.value,
            Name.value,
            Description.value,
            responseDueDate.value
        )
    }

    function UpdateCommand()
    {
        vm.value.clearValidationErrors()
        emit(
            'update-survey',
            SurveyId.value,
            Name.value,
            Description.value,
            responseDueDate.value
        )
    }

    function DeleteCommand()
    {
        emit('delete-survey', SurveyId.value)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="state !== 'Closed'">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="Initialized === false">
                            <i class="fa fa-spinner fa-spin"></i> Loading...
                        </h2>
                        <div v-if="Initialized">
                            <div class="form-group">
                                <label>Name</label>
                                <input type="text" v-model.trim="Name" class="form-control">
                                <div class="error">{{nameError}}</div>
                            </div>
                            <div class="form-group">
                                <label>Description</label>
                                <textarea type="text" v-model.trim="Description" class="form-control"></textarea>
                            </div>
                            <div class="form-group">
                                <label>Response due date:</label>
                                <input type="date" class="form-control" v-model="responseDueDate" />
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer justify-content-between">

                        <div class="dropdown dropup">
                            <button v-if="state === 'Editor'" type="button" class="btn btn-danger" data-toggle="dropdown">Delete</button>
                            <div class="dropdown-menu" role="menu" style="padding: 10px;">
                                <a class="dropdown-item">Cancel</a>
                                <a class="dropdown-item" @click="DeleteCommand">Confirm</a>
                            </div>
                        </div>

                        <div>
                            <button v-if="state === 'Creator'" @click="CreateCommand" type="button" class="btn btn-primary">Create</button>
                            <button v-if="state === 'Editor'" @click="UpdateCommand" type="button" class="btn btn-primary">Save</button>
                            <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>